'use client';

import { CircleAlert } from 'lucide-react';
import type { ComponentProps, ReactElement, ReactNode } from 'react';
import {
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~/components/ui/tooltip';
import { cn } from '~/lib/utils';

export type TooltipProps = {
  children: ReactElement | ReactNode;
  content: ReactNode;
  tooltipClassName?: string;
  anchorClassName?: string;
  delay?: number | null;
  hideIcon?: boolean;
  isNotInteractive?: boolean;
  /** Legacy MUI-shaped props — only origin fields are mapped; extras are ignored. */
  popoverProps?: Record<string, unknown> & {
    anchorOrigin?: { vertical?: string | number; horizontal?: string | number };
    transformOrigin?: { vertical?: string | number; horizontal?: string | number };
  };
  iconProps?: ComponentProps<typeof CircleAlert>;
};

function mapSide(
  vertical?: string | number,
  horizontal?: string | number,
): 'top' | 'bottom' | 'left' | 'right' {
  if (vertical === 'center' || vertical === 'middle') {
    if (horizontal === 'right') return 'right';
    if (horizontal === 'left') return 'left';
  }
  if (vertical === 'top' || (typeof vertical === 'number' && vertical < 0)) return 'top';
  if (vertical === 'bottom') return 'bottom';
  if (horizontal === 'right') return 'right';
  if (horizontal === 'left') return 'left';
  return 'top';
}

function mapAlign(
  side: 'top' | 'bottom' | 'left' | 'right',
  vertical?: string | number,
  horizontal?: string | number,
): 'start' | 'center' | 'end' {
  const axis = side === 'left' || side === 'right' ? vertical : horizontal;
  if (axis === 'left' || axis === 'top') return 'start';
  if (axis === 'right' || axis === 'bottom') return 'end';
  return 'center';
}

export default function Tooltip({
  delay = 500,
  content,
  children,
  anchorClassName,
  popoverProps,
  tooltipClassName,
  hideIcon,
  iconProps,
}: TooltipProps) {
  if (!content) return children;

  const { className: iconClassName, ...otherIconProps } = iconProps || {};
  const originVertical =
    popoverProps?.anchorOrigin?.vertical ?? popoverProps?.transformOrigin?.vertical;
  const originHorizontal =
    popoverProps?.anchorOrigin?.horizontal ?? popoverProps?.transformOrigin?.horizontal;
  const side = mapSide(originVertical, originHorizontal);
  const align = mapAlign(side, originVertical, originHorizontal);

  return (
    <TooltipProvider delayDuration={delay ?? 0}>
      <UiTooltip>
        <TooltipTrigger asChild>
          <div className={cn('inline-flex items-center', anchorClassName)}>
            {children}
            {!hideIcon ? (
              <CircleAlert
                className={cn('text-muted-foreground ml-1 size-4', iconClassName)}
                strokeWidth={2}
                {...otherIconProps}
              />
            ) : null}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          className={cn(
            'max-w-[400px] border border-white/10 bg-neutral-900 px-2 py-1 text-left text-xs font-normal text-white shadow-md',
            tooltipClassName,
          )}
        >
          {content}
        </TooltipContent>
      </UiTooltip>
    </TooltipProvider>
  );
}
